/* Native sticky reading scenes: scroll reveals prose, then releases the chapter. */
(() => {
  'use strict';
  if (window.LRSTextReveal) return;
  const COPY = '[data-lrs-scroll-copy]';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const contrast = matchMedia('(prefers-contrast: more), (forced-colors: active)');
  const copies = new Map();
  const scenes = new Map();
  let frame = 0, needsMeasure = true, initialHashHandled = false;
  const clamp = value => Math.max(0, Math.min(1, value));
  const enabled = () => !reduced.matches && !contrast.matches && document.body.dataset.lrsMotion !== 'false' && !window.Shopify?.designMode;
  function headerInset() {
    // Reserve the measured header height even when its automatic hide/show
    // changes the navigation offset. The reading anchor and word progress stay
    // steady when the visitor reverses the scroll direction.
    const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--lrs-header-height'));
    const navigation = document.body.classList.contains('lrs-vehicle-page') ? document.querySelector('[data-vehicle-navigation]') : null;
    const navigationHeight = navigation && !navigation.hidden ? navigation.getBoundingClientRect().height : 0;
    return Math.max(0, Number.isFinite(value) ? value : document.querySelector('.site-header')?.getBoundingClientRect().height || 76) + navigationHeight + 16;
  }
  function bottomInset(scene) {
    if (innerWidth > 800) return 24;
    const section = scene.closest('[data-chapters]');
    const value = section ? parseFloat(getComputedStyle(section).getPropertyValue('--lrs-chapter-dock-height')) : NaN;
    return Math.max(24, Number.isFinite(value) ? value + 16 : section?.querySelector('.chapter-index')?.getBoundingClientRect().height + 16 || 24);
  }
  function paintWords(record, progress) {
    const feather = 1.75;
    const position = progress * (record.words.length + feather - 1);
    record.words.forEach((word, index) => {
      const value = (progress >= 1 ? 1 : clamp((position - index) / feather)).toFixed(3);
      if (record.values[index] !== value) {
        word.style.setProperty('--lrs-word-opacity', value);
        record.values[index] = value;
      }
    });
    record.element.dataset.lrsRevealProgress = progress.toFixed(3);
  }
  function resetScene(record) {
    record.element.classList.remove('lrs-scroll-scene--pinned');
    record.stage.classList.remove('lrs-scroll-stage--pinned');
    record.element.style.minHeight = record.originalMinHeight;
    record.element.style.removeProperty('--lrs-scene-top');
    record.element.style.removeProperty('--lrs-scene-runway');
    record.pinned = false;
    record.element.dataset.lrsPinMode = 'flow';
  }
  function measure() {
    needsMeasure = false;
    const animate = enabled();
    const viewport = Math.min(innerHeight, window.visualViewport?.height || innerHeight);
    scenes.forEach(record => {
      if (!record.element.isConnected) return;
      const top = headerInset();
      const bottom = bottomInset(record.element);
      const stageHeight = record.stage.getBoundingClientRect().height;
      const available = Math.max(0, viewport - top - bottom);
      const words = record.copies.reduce((total, copy) => total + copy.words.length, 0);
      // A tall stage keeps normal document flow rather than clipping a paragraph
      // or creating an inner scrollbar on short mobile viewports.
      if (!animate || !CSS.supports('position', 'sticky') || stageHeight > available || words < 8) {
        resetScene(record); return;
      }
      const styles = getComputedStyle(record.element);
      record.paddingTop = parseFloat(styles.paddingTop) || 0;
      record.paddingBottom = parseFloat(styles.paddingBottom) || 0;
      record.top = top;
      record.distance = Math.min(viewport * 1.35, Math.max(280, viewport * .62, words * 4));
      record.hold = Math.min(140, Math.max(64, viewport * .12));
      record.pinned = true;
      record.element.classList.add('lrs-scroll-scene--pinned');
      record.stage.classList.add('lrs-scroll-stage--pinned');
      record.element.style.setProperty('--lrs-scene-top', `${top}px`);
      record.element.style.setProperty('--lrs-scene-runway', `${record.distance + record.hold}px`);
      record.element.style.minHeight = `${Math.ceil(stageHeight + record.distance + record.hold + record.paddingTop + record.paddingBottom)}px`;
      record.element.dataset.lrsPinMode = 'sticky';
    });
  }
  function sceneStart(record) {
    return Math.max(0, scrollY + record.element.getBoundingClientRect().top + record.paddingTop - record.top);
  }
  function flowProgress(record, viewport) {
    const rect = record.element.getBoundingClientRect();
    const y = Math.max(0, scrollY);
    if (Number.isFinite(record.anchorAt) && y >= record.anchorAt - 2) return 1;
    const start = Math.max(0, y + rect.top - viewport * .8);
    const distance = Math.max(180, rect.height + viewport * .32);
    return clamp((y - start) / distance);
  }
  function paint() {
    frame = 0;
    if (needsMeasure) measure();
    if (!enabled()) { copies.forEach(record => paintWords(record, 1)); return; }
    const viewport = innerHeight;
    const y = Math.max(0, scrollY);
    const updates = [];
    scenes.forEach(record => {
      if (!record.pinned || !record.element.isConnected) return;
      const progress = clamp((y - sceneStart(record)) / record.distance);
      const count = record.copies.reduce((total, copy) => total + copy.words.length, 0);
      let offset = 0;
      record.copies.forEach(copy => {
        const local = progress >= 1 ? 1 : clamp((progress * count - offset) / copy.words.length);
        updates.push([copy, local]); offset += copy.words.length;
      });
      record.element.dataset.lrsSceneProgress = progress.toFixed(3);
    });
    copies.forEach(record => {
      if (!record.element.isConnected || record.scene?.pinned) return;
      updates.push([record, flowProgress(record, viewport)]);
    });
    updates.forEach(([record, progress]) => paintWords(record, progress));
  }
  function queue() { if (!frame) frame = requestAnimationFrame(paint); }
  const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(() => { needsMeasure = true; queue(); }) : null;
  function split(element) {
    if (copies.has(element)) return copies.get(element);
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        return node.nodeValue.trim() && !node.parentElement.closest('h1,h2,h3,h4,h5,h6,script,style,button,textarea,code,pre,[data-lrs-reveal-skip],[aria-hidden="true"]') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    const nodes = [], words = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(node => {
      const fragment = document.createDocumentFragment();
      (window.LRSI18n?.words(node.nodeValue) || node.nodeValue.split(/(\s+)/u)).forEach(part => {
        if (!part) return;
        if (/^\s+$/u.test(part)) fragment.append(document.createTextNode(part));
        else {
          const word = document.createElement('span');
          word.className = 'lrs-scroll-word'; word.textContent = part;
          fragment.append(word); words.push(word);
        }
      });
      node.replaceWith(fragment);
    });
    if (!words.length) return null;
    element.removeAttribute('data-reveal');
    const record = {element, words, values: [], scene: null, anchorAt: null};
    copies.set(element, record); resize?.observe(element);
    return record;
  }
  function registerScene(copy) {
    const element = copy.element.closest('[data-lrs-scroll-scene]');
    const stage = element?.querySelector('[data-lrs-scroll-stage]');
    if (!element || !stage || !stage.contains(copy.element)) return;
    let record = scenes.get(element);
    if (!record) {
      record = {element, stage, copies: [], originalMinHeight: element.style.minHeight, pinned: false, paddingTop: 0, paddingBottom: 0, top: 0, distance: 0};
      scenes.set(element, record); resize?.observe(stage);
      // Legacy fade hooks on complete editorial panels are superseded by prose.
      stage.removeAttribute('data-reveal');
      stage.querySelectorAll('[data-reveal]').forEach(node => node.removeAttribute('data-reveal'));
    }
    if (!record.copies.includes(copy)) record.copies.push(copy);
    record.copies.sort((a, b) => a.element === b.element ? 0 : a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1);
    copy.scene = record;
  }
  function init(scope = document) {
    if (!enabled()) return;
    const roots = [...scope.querySelectorAll(COPY)];
    if (scope.matches?.(COPY)) roots.unshift(scope);
    roots.filter(root => !root.parentElement.closest(COPY)).forEach(root => {
      const copy = split(root); if (copy) registerScene(copy);
    });
    needsMeasure = true;
    paint();
    if (!initialHashHandled) {
      initialHashHandled = true;
      if (location.hash) requestAnimationFrame(() => jumpToHash(location.hash, 'auto'));
    }
  }
  function refresh() {
    if (enabled()) init();
    needsMeasure = true; queue();
  }
  function hashTarget(hash) {
    if (!hash || hash.length < 2) return null;
    let id; try { id = decodeURIComponent(hash.slice(1)); } catch { return null; }
    const target = document.getElementById(id);
    return target?.closest('[data-lrs-scroll-scene]') || null;
  }
  function jumpToHash(hash, behavior = 'auto') {
    const element = hashTarget(hash);
    const record = element ? scenes.get(element) : null;
    if (!record || !enabled()) return false;
    if (needsMeasure) measure();
    const top = record.pinned ? Math.ceil(sceneStart(record) + record.distance) : Math.max(0, scrollY + element.getBoundingClientRect().top - headerInset());
    if (!record.pinned) record.copies.forEach(copy => {copy.anchorAt = top; paintWords(copy, 1);});
    window.scrollTo({top, behavior});
    queue(); return true;
  }
  function unload(scope) {
    copies.forEach((record, element) => {
      if (scope !== element && !scope.contains(element)) return;
      resize?.unobserve(element); copies.delete(element);
    });
    scenes.forEach((record, element) => {
      if (scope !== element && !scope.contains(element)) return;
      resetScene(record); resize?.unobserve(record.stage); scenes.delete(element);
    });
    needsMeasure = true; queue();
  }
  window.LRSTextReveal = {init, refresh};
  addEventListener('scroll', () => {if (copies.size && enabled()) queue();}, {passive: true});
  addEventListener('resize', refresh, {passive: true});
  window.visualViewport?.addEventListener('resize', refresh, {passive: true});
  addEventListener('pageshow', refresh);
  addEventListener('hashchange', () => requestAnimationFrame(() => jumpToHash(location.hash, 'auto')));
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.target && link.target !== '_self') return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || !hashTarget(url.hash)) return;
    if (!jumpToHash(url.hash, 'auto')) return;
    event.preventDefault();
    if (location.hash !== url.hash) history.pushState(null, '', url.hash);
  });
  reduced.addEventListener('change', refresh); contrast.addEventListener('change', refresh);
  document.addEventListener('lrs:headerchange', refresh);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  document.addEventListener('shopify:section:reorder', refresh);
  document.fonts?.ready.then(refresh);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true}); else init();
})();
